import { HasProduct, ProductContextModel, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSearchDataModeActive } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import ProductContextAvailability from "@thos/Overrides/Components/ProductContextAvailability";
import ProductListProductImage from "@thos/Overrides/Widgets/ProductList/ProductListProductImage";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct & WidgetProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    searchDataModeActive: getSearchDataModeActive(state),
});

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    stockAvailabilityGrid?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    availabilityStyles?: ProductAvailabilityStyles;
    availabilityDataGridItem?: GridItemProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 15px;
            padding-left: 15px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0px 15px 15px 15px;
            > div {
                padding: 15px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px;
                        `,
                        css`
                            padding: 0px;
                        `,
                        css`
                            padding: 0px;
                        `,
                        css``,
                        css``,
                    ])}
                >div {
                    > div {
                        > div {
                            > div {
                                ${({ theme }: { theme: BaseTheme }) =>
                                    breakpointMediaQueries(theme, [
                                        css`
                                            flex-direction: column;
                                        `,
                                        css`
                                            flex-direction: column;
                                        `,
                                        css`
                                            flex-direction: column;
                                        `,
                                        css`
                                            flex-direction: column;
                                        `,
                                        css`
                                            flex-direction: row;
                                        `,
                                    ])}
                            }
                        }
                    }
                }
            }
        `,
        align: "middle",
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    stockAvailabilityGrid: {
        width: 12,
        css: css`
            padding: 0px;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
    rightColumnGridContainer: {
        css: css`
            height: 100%;
        `,
        offsetCss: css`
            margin: 0;
            width: 100% !important;
            height: 100%;
        `,
    },
    availabilityStyles: {
        messageText: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
    availabilityDataGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    return (
        <ProductListProductContext product={props.product} key={props.product.id}>
            <GridContainer {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
                <GridItem {...styles.leftColumnGridItem}>
                    {props.product.isSponsored && (
                        <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                    )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={props.showCompare}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    showImageBadges={props.showImageBadges}
                    showTextBadges={props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                />
                </GridItem>

                <GridItem {...styles.rightColumnGridItem}>
                    <GridContainer {...styles.rightColumnGridContainer} data-test-selector="herenow">
                        <GridItem {...styles.infoGridItem}>
                            <ProductListProductInformation
                                showBrand={props.showBrand}
                                showTitle={props.showTitle}
                                showPartNumbers={props.showPartNumbers}
                                showAvailability={props.showAvailability}
                                showAttributes={props.showAttributes}
                                showImageBadges={props.showImageBadges}
                                maxImageBadges={props.maximumNumberOfImageBadges}
                                showTextBadges={props.showTextBadges}
                                maxTextBadges={props.maximumNumberOfTextBadges}
                            />
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <GridItem {...styles.stockAvailabilityGrid}>
                                <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                            </GridItem>
                            <ProductListActions showPrice={true} showAddToList={props.showAddToList} />
                        </GridItem>
                        {props.searchDataModeActive && (
                            <GridItem {...styles.searchScoreDataGridItem}>
                                <ProductSearchScoreData />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </ProductListProductContext>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductCard));
